import React from "react";
import "../styles.scss";
import { Text, H1 } from "@amzn/stencil-react-components/text";
import { Row } from "@amzn/stencil-react-components/layout";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { StatusContainer } from "./status-container";
import { SdaInfo } from "../../../utility/application-data";
import { JobDetailsModel } from "../../../utility/application-data";
import { IconCalendarFill } from "@amzn/stencil-react-components/icons";
import { processTime } from "../../../utility/time-processor";
import useArb from "../../../hooks/use-arb";
import ICalendarLink from "react-icalendar-link";
import moment from "moment";
import { getProcessedJCAddress, isVirtualJob } from "../../../utility/jc-response-helper";
import { useParams } from "react-router-dom";
import { isSalesforceApplication } from "../../../helpers/url-params";
import { GoogleMapLink } from "../../google-map-link";

interface StartDateHeaderProps {
  sdaDetails?: SdaInfo;
  jobDetails: JobDetailsModel;
  onViewDetailsClick: () => void;
  isTentativeStart: boolean;
}

function getStartDateDifference(startDateAndStartTime: string, timezone: string, timeFormat: string) {
  const dateTzWithStartTime = moment.tz(startDateAndStartTime, timeFormat, timezone);
  const currDate = new Date();
  const currentDateTime = moment.tz(currDate, timezone);
  const startDateDifference = dateTzWithStartTime.diff(currentDateTime, "day");
  return {
    startDateDifference,
    dateTzWithStartTime,
  };
}

export const FirstDayHeader = ({
  sdaDetails,
  jobDetails,
  onViewDetailsClick,
  isTentativeStart,
}: StartDateHeaderProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  let plainStartDateAndStartTime;
  let startDateDifference;
  let dateTzWithStartTime;

  const { applicationId } = useParams<{ applicationId: string }>();

  if (!isSalesforceApplication(applicationId) && sdaDetails) {
    //for jobs onboarded to DSP
    const startTime = sdaDetails.startTime;
    const processedStartTime = processTime(startTime);
    const startDateAndStartTime = jobDetails.firstDayOnSite + " " + processedStartTime;
    const timeFormat = "YYYY-MM-DD hh:mm A";
    plainStartDateAndStartTime = moment(startDateAndStartTime, timeFormat); // create time without time zone to preserve site time
    const dateDiff = getStartDateDifference(startDateAndStartTime, sdaDetails.timezone, timeFormat);
    startDateDifference = dateDiff.startDateDifference;
    dateTzWithStartTime = dateDiff.dateTzWithStartTime;
  } else {
    //logic for salesforce job. Date returned from SF is in a different format compared to SDA
    plainStartDateAndStartTime = moment(jobDetails.firstDayOnSiteL10N, "YYYY-MM-DD").toDate();
    const timeFormat = "YYYY-MM-DD";
    const localTimeZone = moment.tz.guess();
    const dateDiff = getStartDateDifference(jobDetails.firstDayOnSiteL10N, localTimeZone, timeFormat);
    startDateDifference = dateDiff.startDateDifference;
    dateTzWithStartTime = dateDiff.dateTzWithStartTime;
  }

  const dateTranslated = bundle.formatMessage("Checklist-AppointmentCard-Month-Day-Year", {
    dateParam: plainStartDateAndStartTime,
  });

  const dateTimeTranslated = bundle.formatMessage("Checklist-AppointmentCard-DateTime", {
    dateParam: plainStartDateAndStartTime,
  });

  const localDateTime = sdaDetails ? dateTimeTranslated : dateTranslated;
  const addToCalendar = bundle.getMessage("Checklist-AppointmentCard-ButtonText3");
  const viewDetails = bundle.getMessage("Checklist-AppointmentCard-ButtonText1");
  const appointmentTitle = bundle.getMessage("Checklist-CardControlled-FirstDayTitle");

  const firstDayCalendarEvent = {
    title: appointmentTitle,
    startTime: dateTzWithStartTime.format(),
    location: jobDetails && getProcessedJCAddress(jobDetails),
  };

  const largeViewImage = startDateDifference > 0 ? require("./star.svg").default : require("./backpack.svg").default;
  const smallViewPortImage =
    startDateDifference > 0 ? require("./star.svg").default : require("./backpack.svg").default;

  let headerText;
  if (startDateDifference > 0) {
    if (isTentativeStart) {
      headerText = bundle.formatMessage("Checklist-AppointmentCard-First-Day-Message3", {
        days: startDateDifference,
      });
    } else {
      headerText = bundle.formatMessage("Checklist-AppointmentCard-First-Day-Message1", {
        days: startDateDifference,
      });
    }
  } else {
    headerText = bundle.getMessage("Checklist-AppointmentCard-First-Day-Message2");
  }

  const addToCalendarKeyDown = (event: any) => {
    if (event.key === " " || event.code === "Space") {
      event.preventDefault();
      (document.querySelector(".add-to-calendar") as any).click();
    }
  };

  return (
    <StatusContainer largeViewPortImage={largeViewImage} smallViewPortImage={smallViewPortImage}>
      <H1 fontSize={"T400"} fontWeight={"regular"} textAlign="center" id="main-content" className="first-day-header">
        {headerText}
      </H1>

      <Row gridGap={"1rem"}>
        <Row gridGap={"0.3rem"} alignItems={"center"}>
          <IconCalendarFill />
          <Text fontSize={"T200"}>{localDateTime}</Text>
        </Row>
      </Row>

      <GoogleMapLink isVirtual={isVirtualJob(jobDetails)} address={jobDetails && getProcessedJCAddress(jobDetails)} />
      <Row gridGap={"1rem"}>
        <Button
          as={"Col"}
          size={ButtonSize.Small}
          onClick={() => (document.querySelector(".add-to-calendar") as any).click()}
          onKeyDown={(event) => addToCalendarKeyDown(event)}
          tabIndex={-1}
        >
          <ICalendarLink
            key="calendar-button"
            filename={appointmentTitle}
            event={firstDayCalendarEvent}
            className={"add-to-calendar"}
          >
            {addToCalendar}
          </ICalendarLink>
        </Button>
        <Button variant={ButtonVariant.Primary} size={ButtonSize.Small} onClick={onViewDetailsClick}>
          {viewDetails}
        </Button>
      </Row>
    </StatusContainer>
  );
};
